import { createAuthenticatedFetch } from "@assetwatch/auth";
import type { AppType } from "@assetwatch/enterprise-backend/app";
import { hc } from "hono/client";
import i18n from "../i18n/config";
import {
  addEnterpriseApiBreadcrumb,
  captureAskReliError,
  createEnterpriseRequestId,
} from "../lib/sentry";
export class EnterpriseApiError extends Error {
  readonly status: number;
  readonly operation?: string;

  constructor(
    message: string,
    status: number,
    options?: { operation?: string },
  ) {
    super(message);
    this.name = "EnterpriseApiError";
    this.status = status;
    this.operation = options?.operation;
  }
}

export type ReliChatMessage = {
  role: "user" | "assistant";
  content: string;
  /** Preserve assistant clarification state in the conversation history too. */
  clarification?: ReliActiveClarification | null;
};
type ReliClarificationSelectionBase = {
  clarificationId: string;
  question: string;
};

type ReliClarificationOption = {
  id: string;
  label: string;
};

export type ReliClarificationSelection =
  | (ReliClarificationSelectionBase & {
      mode: "select";
      option: ReliClarificationOption;
    })
  | (ReliClarificationSelectionBase & {
      mode: "multi_select";
      options: ReliClarificationOption[];
    })
  | (ReliClarificationSelectionBase & {
      mode: "free_text";
      response: string;
    });
export type ReliActiveClarification = {
  id: string;
  question: string;
  mode?: "select" | "multi_select" | "free_text";
  options: ReliClarificationOption[];
};

const configuredApiBaseUrl = (
  import.meta.env.VITE_ENTERPRISE_API_BASE_URL as string | undefined
)?.replace(/\/$/, "");
const configuredApiPathPrefix = (
  import.meta.env.VITE_ENTERPRISE_API_PATH_PREFIX as string | undefined
)?.trim();
const branchName = (import.meta.env.VITE_BRANCH_NAME as string | undefined)
  ?.toLowerCase()
  .replace(/\//g, "-");
const deployEnvironment =
  (import.meta.env.VITE_DEPLOY_ENVIRONMENT as string | undefined) ?? "dev";

function getDefaultApiBaseUrl() {
  if (deployEnvironment === "prod") {
    return `https://enterprise-app.master.${deployEnvironment}.assetwatch.com`;
  }
  const branch = branchName === "qa" ? "qa" : "dev";
  return `https://enterprise-app.${branch}.${deployEnvironment}.assetwatch.com`;
}

const apiBaseUrl = configuredApiBaseUrl ?? getDefaultApiBaseUrl();
const apiPathPrefix =
  configuredApiPathPrefix === null || configuredApiPathPrefix === undefined
    ? "/enterprise"
    : configuredApiPathPrefix === ""
      ? ""
      : configuredApiPathPrefix.startsWith("/")
        ? configuredApiPathPrefix.replace(/\/$/, "")
        : `/${configuredApiPathPrefix.replace(/\/$/, "")}`;

function getEnterpriseRequestLanguage() {
  const language = i18n.resolvedLanguage ?? i18n.language ?? "en";
  const normalized = language.trim().toLowerCase().split("-")[0];
  return ["en", "es", "ja"].includes(normalized) ? normalized : "en";
}

const authenticatedFetch = createAuthenticatedFetch({
  createRequestId: createEnterpriseRequestId,
  getAdditionalHeaders: () => ({
    "Accept-Language": getEnterpriseRequestLanguage(),
  }),
  onBreadcrumb: addEnterpriseApiBreadcrumb,
});

const client = hc<AppType>(`${apiBaseUrl}${apiPathPrefix}`, {
  fetch: authenticatedFetch,
});

function customerParam(customerId: number) {
  return { customerId };
}

function facilityParam(customerId: number, facilityId: number) {
  return {
    customerId,
    facilityId,
  };
}

export async function fetchEnterpriseReleaseInfo() {
  const res = await client.v1.internal["release-info"].$get();
  const status: number = res.status;

  if (status === 404) {
    return null;
  }

  if (!res.ok) {
    throw new EnterpriseApiError(
      `Enterprise release info request failed with ${status}`,
      status,
      { operation: "release-info" },
    );
  }

  return res.json();
}

export async function fetchEnterpriseCustomerContext(
  externalCustomerId: string,
) {
  const res = await client.v1["customer-context"].$get({
    query: { externalCustomerId },
  });
  if (!res.ok)
    throw new EnterpriseApiError(
      `Enterprise customer lookup failed with ${res.status}`,
      res.status,
      { operation: "customer-context" },
    );
  return res.json();
}

export async function fetchSelectableEnterpriseCustomers() {
  const res = await client.v1.customers.$get();
  if (!res.ok)
    throw new EnterpriseApiError(
      `Enterprise customer list request failed with ${res.status}`,
      res.status,
    );
  return res.json();
}

export async function fetchEnterpriseOverview(customerId: number) {
  const res = await client.v1.customers[":customerId"].overview.$get({
    param: customerParam(customerId),
  });
  if (!res.ok)
    throw new EnterpriseApiError(
      `Enterprise overview request failed with ${res.status}`,
      res.status,
    );
  return res.json();
}

export async function fetchRoiVsSpend(customerId: number) {
  const res = await client.v1.customers[":customerId"]["roi-vs-spend"].$get({
    param: customerParam(customerId),
  });
  if (!res.ok)
    throw new EnterpriseApiError(
      `ROI vs spend request failed with ${res.status}`,
      res.status,
    );
  return res.json();
}

export async function fetchHealthScore(customerId: number) {
  const res = await client.v1.customers[":customerId"]["health-score"].$get({
    param: customerParam(customerId),
  });
  if (!res.ok)
    throw new EnterpriseApiError(
      `Health score request failed with ${res.status}`,
      res.status,
    );
  return res.json();
}

export async function fetchFacilityMaturityEvents(
  customerId: number,
  facilityId: number,
) {
  const res = await client.v1.customers[":customerId"].facilities[
    ":facilityId"
  ]["maturity-events"].$get({
    param: facilityParam(customerId, facilityId),
  });
  if (!res.ok) {
    throw new EnterpriseApiError(
      `Facility maturity events request failed with ${res.status}`,
      res.status,
    );
  }
  return res.json();
}

export async function fetchProgramSummary(
  customerId: number,
  durationDays: number,
) {
  const res = await client.v1.customers[":customerId"]["program-summary"].$get({
    param: customerParam(customerId),
    query: { duration: String(durationDays) },
  });
  if (!res.ok) {
    throw new EnterpriseApiError(
      `Program summary request failed with ${res.status}`,
      res.status,
    );
  }
  return res.json();
}

export async function fetchItemsOfConcern(customerId: number) {
  const res = await client.v1.customers[":customerId"]["items-of-concern"].$get(
    {
      param: customerParam(customerId),
    },
  );
  if (!res.ok)
    throw new EnterpriseApiError(
      `Items of concern request failed with ${res.status}`,
      res.status,
    );
  return res.json();
}

export async function fetchRecentWins(customerId: number) {
  const res = await client.v1.customers[":customerId"]["recent-wins"].$get({
    param: customerParam(customerId),
  });
  if (!res.ok)
    throw new EnterpriseApiError(
      `Recent wins request failed with ${res.status}`,
      res.status,
    );
  return res.json();
}

export async function fetchAchievements(customerId: number) {
  const res = await client.v1.customers[":customerId"].achievements.$get({
    param: customerParam(customerId),
  });
  if (!res.ok)
    throw new EnterpriseApiError(
      `Achievements request failed with ${res.status}`,
      res.status,
    );
  return res.json();
}

export async function sendReliMessage(
  customerId: number,
  message: string,
  history: ReliChatMessage[],
  clarificationSelection?: ReliClarificationSelection,
  activeClarification?: ReliActiveClarification,
) {
  const chatClient = client.v1.customers[":customerId"].chat;
  let res: Awaited<ReturnType<typeof chatClient.$post>>;
  try {
    res = await chatClient.$post({
      param: customerParam(customerId),
      json: { message, history, clarificationSelection, activeClarification },
    });
  } catch (err) {
    captureAskReliError(err, { customerId, phase: "fetch" });
    throw err instanceof Error ? err : new Error(String(err));
  }
  const requestId = res.headers.get("X-Request-Id") ?? undefined;
  if (!res.ok) {
    const responsePreview = await res.text().catch(() => "");
    const err = new EnterpriseApiError(
      `Ask Reli request failed with ${res.status}`,
      res.status,
    );
    captureAskReliError(err, {
      customerId,
      phase: "http_response",
      requestId,
      httpStatus: res.status,
      responsePreview: responsePreview.slice(0, 2000),
    });
    throw err;
  }
  try {
    return await res.json();
  } catch (err) {
    captureAskReliError(err, { customerId, phase: "json_parse", requestId });
    throw err instanceof Error ? err : new Error(String(err));
  }
}

export function getEnterpriseReliabilityScore(
  response: Awaited<ReturnType<typeof fetchHealthScore>> | undefined | null,
): number | null {
  if (!response) return null;
  const summaryAvg = response.portfolioSummary?.averageOverallScore;
  if (summaryAvg !== null) return summaryAvg;
  const scores =
    response.facilities
      ?.map((f) => f.overallScore)
      .filter((s): s is number => s !== null) ?? [];
  if (scores.length === 0) return null;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

// Inferred types from the Hono RPC client

export type EnterpriseSelectableCustomer = Awaited<
  ReturnType<typeof fetchSelectableEnterpriseCustomers>
>[number];
export type EnterpriseCustomerContextResponse = Awaited<
  ReturnType<typeof fetchEnterpriseCustomerContext>
>;
export type EnterpriseOverviewResponse = Awaited<
  ReturnType<typeof fetchEnterpriseOverview>
>;
export type RoiVsSpendResponse = Awaited<ReturnType<typeof fetchRoiVsSpend>>;
export type HealthScoreResponse = Awaited<ReturnType<typeof fetchHealthScore>>;
export type FacilityMaturityEventsResponse = Awaited<
  ReturnType<typeof fetchFacilityMaturityEvents>
>;
export type ProgramSummaryResponse = Awaited<
  ReturnType<typeof fetchProgramSummary>
>;
export type ItemsOfConcernResponse = Awaited<
  ReturnType<typeof fetchItemsOfConcern>
>;
export type RecentWinsResponse = Awaited<ReturnType<typeof fetchRecentWins>>;
export type AchievementsResponse = Awaited<
  ReturnType<typeof fetchAchievements>
>;
export type ReliChatResponse = Awaited<ReturnType<typeof sendReliMessage>>;

export type HealthScoreFacility = HealthScoreResponse["facilities"][number];
export type MaturityTier = NonNullable<HealthScoreFacility["maturityTier"]>;
export type PortfolioSummary = HealthScoreResponse["portfolioSummary"];
export type GlobalBenchmarks = HealthScoreResponse["globalBenchmarks"];
export type EnterpriseOverviewFacility =
  EnterpriseOverviewResponse["facilities"][number];
export type RoiVsSpendPoint = RoiVsSpendResponse["series"][number];
export type RoiVsSpendCompliance = RoiVsSpendResponse["compliance"];
export type FacilityMaturityEvent =
  FacilityMaturityEventsResponse["events"][number];
export type ProgramSummaryTopWin =
  ProgramSummaryResponse["value"]["topWins"][number];
export type ItemOfConcern = ItemsOfConcernResponse["items"][number];
export type RecentWin = RecentWinsResponse["wins"][number];
export type AchievementBadge = AchievementsResponse["badges"][number];
export type AchievementTier = AchievementBadge["tiers"][number];
export type AchievementFacility = AchievementTier["facilities"][number];
export type FixItVisualization = NonNullable<ReliChatResponse["visualization"]>;
export type FixItVisualizationPoint = FixItVisualization["points"][number];
export type PortfolioOperationalAverages =
  PortfolioSummary["operationalAverages"];
